import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { loadScriptOnConsent } from "./CookieBanner";

const DEFAULT_GA_ID = "G-R8B3NSXD0W";
const GA_ID_PATTERN = /^(G-[A-Z0-9]+|UA-\d+-\d+|AW-\d+|GTM-[A-Z0-9]+)$/i;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function injectGA(gaId: string) {
  if (typeof window === "undefined") return;
  if (document.getElementById("ga-src")) return;

  const s = document.createElement("script");
  s.id = "ga-src";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", gaId, { anonymize_ip: true });
}

export function AnalyticsLoader() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: gaId } = useQuery({
    queryKey: ["site_settings", "google_analytics_id"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "google_analytics_id")
        .maybeSingle();
      if (!data) return DEFAULT_GA_ID; // not configured → default
      const v = typeof data.value === "string" ? data.value.trim() : "";
      if (v === "") return null; // explicitly disabled
      if (GA_ID_PATTERN.test(v)) return v;
      return null;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (gaId === undefined) return;
    if (!gaId) return;
    loadScriptOnConsent("analytics", () => injectGA(gaId));
  }, [gaId]);

  // Track SPA route changes
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname]);

  return null;
}